import React, { useCallback } from 'react';

import { View } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import { StoreDiagnosticsModal } from 'features/store/components/store-diagnostic-modal';
import { StyleSheet } from 'react-native';

import Dialog from 'components/dialog';
import { ModalAuth } from 'components/modal-auth';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useAuthContext } from 'state/auth';
import { ModalAuthTrigger } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { MaybeShowOnboardingContent } from 'state/onboarding';
import { useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';
import { isIos, isWeb } from 'utils/environment';

const USE_GLOBAL_APP_MODALS_IN_CARD_MODAL = isIos;

// TODO: After we define the new store order unavailable flow we can remove or place it in the store-order-unavailable-dialog component instead.
// https://rbictg.atlassian.net/browse/BKPE-22529
const StoreOrderUnavailableDialog = () => {
  const { setIsStoreOrderUnavailableDialogOpen } = useUIContext();

  const handleCloseAutoSelectPrompt = () => {
    setIsStoreOrderUnavailableDialogOpen(false);
  };

  return (
    <Dialog
      showDialog
      heading={t('storeClosed')}
      body={t('cannotPlaceOrder')}
      confirmButtonLabel={t('ok')}
      confirmDialog={handleCloseAutoSelectPrompt}
      onDismiss={handleCloseAutoSelectPrompt}
      modalAppearanceEventMessage="Ordering is unavailable"
      isErrorModal
    />
  );
};

/** put modals that should be globally available in the app here.
 * NOTE this is also mounted in <CardModal/> on ios so they can show up in an iOS modal
 */
export const GlobalAppModals = ({ isModal = false }) => {
  const { modalAuthState, modalAuthIsOpen } = useAuthContext();

  const {
    recent: { unavailablePendingRecentItem, setUnavailablePendingRecentItem },
  } = useOrderContext();

  const {
    onConfirmStoreChange,
    onConfirmStoreChangeParams,
    clearOnConfirmStoreChangeParams,
  } = useStoreContext();

  const { isStoreOrderUnavailableDialogOpen } = useUIContext();

  const handleConfirmUnavailablePendingRecentItem = useCallback(() => {
    setUnavailablePendingRecentItem(null);
  }, [setUnavailablePendingRecentItem]);

  const handleOnConfirmCb = () => {
    onConfirmStoreChange(onConfirmStoreChangeParams);
    clearOnConfirmStoreChangeParams();
  };

  const shouldDisplayUnavailableModal =
    !!unavailablePendingRecentItem || !!onConfirmStoreChangeParams?.unavailableCartEntries?.length;

  return (
    <>
      {shouldDisplayUnavailableModal && (
        <View testID="app-modals-layout" {...(isWeb ? styles.containerWeb : {})}>
          {unavailablePendingRecentItem && (
            <RemoveUnavailableItemsModal
              unavailableCartEntries={[unavailablePendingRecentItem]}
              onConfirmCb={handleConfirmUnavailablePendingRecentItem}
            />
          )}
          {onConfirmStoreChangeParams?.unavailableCartEntries?.length && (
            <RemoveUnavailableItemsModal
              onConfirmCb={handleOnConfirmCb}
              unavailableCartEntries={onConfirmStoreChangeParams?.unavailableCartEntries}
              unavailableChildrenCartEntries={
                onConfirmStoreChangeParams?.unavailableChildrenCartEntries
              }
            />
          )}
        </View>
      )}

      {modalAuthIsOpen && getModalAuthComponent(modalAuthState.trigger, isModal)}
      {isStoreOrderUnavailableDialogOpen && <StoreOrderUnavailableDialog />}
      <StoreDiagnosticsModal />
    </>
  );
};

/** global app modals that only mount in the root of the app, not in <CardModal/> sub-modals on ios */
export const RootOnlyGlobalAppModals = () => {
  return <MaybeShowOnboardingContent />;
};

// only applicable to very nuanced iOS deep link, no auth situations
// needed where we can't have auth displaying on two screens (modal and non modal) at once
function getModalAuthComponent(trigger: ModalAuthTrigger | undefined, isModal: boolean) {
  if (!trigger) {
    return null;
  }

  if (
    !USE_GLOBAL_APP_MODALS_IN_CARD_MODAL &&
    (trigger === ModalAuthTrigger.Modal || trigger === ModalAuthTrigger.Global)
  ) {
    return <ModalAuth />;
  }

  if (trigger === ModalAuthTrigger.Global && !isModal) {
    return <ModalAuth />;
  }

  if (trigger === ModalAuthTrigger.Modal && isModal) {
    return <ModalAuth />;
  }
  return null;
}

const styles = StyleSheet.create({
  containerWeb: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
});
